/**
 * FirstRunGuide — the floating new-tenant activation guide.
 *
 * Reads all state from FirstRunGuideContext (durable, survives route changes).
 * Three visual states:
 *   • expanded    — a calm card with the current step + next best action
 *   • collapsed   — a small "Setup guide" pill (the reopen affordance after the
 *                   user hides it)
 *   • celebration — a one-time "you're set up" card shown exactly once when
 *                   every required step completes, then retired for good
 *
 * No gradients, no emojis, no full-screen takeover. CTAs advance + navigate;
 * they never dismiss the guide. Respects reduced motion.
 */

import { createPortal } from 'react-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { ChevronRight, ChevronLeft, X, Check, Compass } from 'lucide-react'
import { useFirstRunGuide } from '../../contexts/FirstRunGuideContext'

export function FirstRunGuide() {
  const guide = useFirstRunGuide()
  const reduce = useReducedMotion()

  if (!guide.enabled) return null

  const card = guide.justCompleted
    ? <CompletionCard key="celebration" onDone={guide.acknowledgeCompletion} reduce={!!reduce} />
    : guide.status === 'dismissed'
      ? <CollapsedPill key="pill" onReopen={guide.reopen} done={guide.doneCount} total={guide.totalSteps} reduce={!!reduce} />
      : <ExpandedCard key="card" guide={guide} reduce={!!reduce} />

  return createPortal(
    // pointer-events-auto is required: Radix sets `body { pointer-events: none }`
    // while a modal (e.g. Create Employee) is open, and only re-enables it on its
    // own portalled content — without this, the guide is visible but unclickable
    // whenever a modal is open.
    <div className="fixed bottom-6 right-6 z-[9000] pointer-events-auto">
      <AnimatePresence mode="wait">{card}</AnimatePresence>
    </div>,
    document.body,
  )
}

// ── Collapsed pill (reopen entry point) ───────────────────────────────────────

function CollapsedPill({
  onReopen, done, total, reduce,
}: { onReopen: () => void; done: number; total: number; reduce: boolean }) {
  return (
    <motion.button
      type="button"
      onClick={onReopen}
      initial={reduce ? false : { opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? undefined : { opacity: 0, y: 8, scale: 0.98 }}
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-center gap-2 rounded-full bg-card border border-border shadow-lg pl-3 pr-3.5 py-2 hover:bg-background transition-colors interactive"
      aria-label="Resume setup guide"
    >
      <Compass className="w-4 h-4 text-accent" />
      <span className="text-[12.5px] font-semibold text-foreground">Resume setup</span>
      <span className="text-[11px] font-medium text-muted-foreground tabular-nums">{done}/{total}</span>
    </motion.button>
  )
}

// ── One-time completion card ─────────────────────────────────────────────────

function CompletionCard({ onDone, reduce }: { onDone: () => void; reduce: boolean }) {
  return (
    <motion.div
      role="dialog"
      aria-label="Setup complete"
      initial={reduce ? false : { opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? undefined : { opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className="w-[336px] bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
    >
      <div className="px-5 pt-4 pb-3.5 border-b border-border flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Check className="w-3.5 h-3.5 text-emerald-600" />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[color:var(--color-text-tertiary)]">
            Setup complete
          </p>
        </div>
        <button
          type="button"
          onClick={onDone}
          className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-panel transition-colors shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-5 py-4">
        <h3 className="text-[15px] font-semibold text-foreground leading-snug">You're fully set up</h3>
        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
          Your first hire's onboarding is live and every team is coordinated. This guide won't reappear —
          Overview now tracks readiness, upcoming starters and anything that needs attention.
        </p>
        <button
          type="button"
          onClick={onDone}
          className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-accent hover:bg-accent-hover text-white h-8 px-3.5 text-[12.5px] font-semibold transition-colors interactive"
        >
          Got it
        </button>
      </div>
    </motion.div>
  )
}

// ── Expanded card ──────────────────────────────────────────────────────────────

function ExpandedCard({
  guide, reduce,
}: { guide: ReturnType<typeof useFirstRunGuide>; reduce: boolean }) {
  const { activeStep, activeIndex, totalSteps, doneCount, steps, leadingEdge, launchCandidate } = guide
  const pct = Math.round((doneCount / totalSteps) * 100)
  const isLast = activeIndex === totalSteps - 1

  // The "launch" step only has one honest destination once we know which hire
  // still needs it: their own profile, ready to apply a blueprint. Without a
  // candidate (e.g. signals mid-refresh) it falls back to the generic copy.
  const isLaunchStep = activeStep.id === 'launch'
  const stepCta = isLaunchStep && launchCandidate ? 'Apply a blueprint' : activeStep.cta
  const stepBody = isLaunchStep && launchCandidate
    ? `Launching schedules every task, for every team, against ${launchCandidate.name.split(' ')[0]}'s real start date — nothing left to chase manually. This opens their profile with blueprints ready to apply.`
    : activeStep.body

  return (
    <motion.div
      role="dialog"
      aria-label={activeStep.title}
      aria-live="polite"
      initial={reduce ? false : { opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? undefined : { opacity: 0, y: 8, scale: 0.97 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className="w-[336px] bg-card rounded-2xl shadow-2xl border border-border overflow-hidden"
    >
      {/* Header — calm, no gradient */}
      <div className="px-5 pt-4 pb-3.5 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[color:var(--color-text-tertiary)]">
              Getting started
            </p>
            <p className="mt-1 text-[11px] font-medium text-muted-foreground tabular-nums">
              Step {activeIndex + 1} of {totalSteps}
            </p>
          </div>
          <button
            type="button"
            onClick={guide.dismiss}
            className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-panel transition-colors shrink-0"
            aria-label="Hide guide"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Subtle progress bar */}
        <div className="mt-2.5 h-[3px] w-full rounded-full overflow-hidden bg-border">
          <div
            className="h-full rounded-full bg-accent transition-all duration-700 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        <h3 className="text-[15px] font-semibold text-foreground leading-snug">{activeStep.title}</h3>
        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{stepBody}</p>

        {/* Primary action */}
        <div className="mt-4 flex items-center gap-2">
          {stepCta && activeStep.ctaPath ? (
            <button
              type="button"
              onClick={() => guide.runCta(activeStep)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-accent hover:bg-accent-hover text-white h-8 px-3.5 text-[12.5px] font-semibold transition-colors interactive"
            >
              {stepCta}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={guide.next}
              className="inline-flex items-center gap-1.5 rounded-xl bg-accent hover:bg-accent-hover text-white h-8 px-3.5 text-[12.5px] font-semibold transition-colors interactive"
            >
              {isLast ? 'Finish' : 'Start'}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}

          {activeStep.optional && (
            <button
              type="button"
              onClick={guide.next}
              className="text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip for now
            </button>
          )}
        </div>
      </div>

      {/* Footer — step rail + prev/next */}
      <div className="px-5 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5" role="group" aria-label="Guide progress">
          {steps.map((s, i) => {
            const done = guide.isStepDone(s)
            const current = i === activeIndex
            const locked = i > leadingEdge
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => guide.goToStep(i)}
                disabled={locked}
                aria-current={current ? 'step' : undefined}
                aria-label={locked ? `${s.title} (not yet available)` : `${s.title}${done ? ' (done)' : ''}`}
                className={[
                  'h-1.5 rounded-full transition-all duration-150',
                  locked ? 'cursor-not-allowed' : '',
                  current ? 'w-4 bg-accent'
                    : done ? 'w-1.5 bg-emerald-400'
                    : 'w-1.5 bg-border',
                ].join(' ')}
              />
            )
          })}
        </div>
        <div className="flex items-center gap-1.5">
          {activeIndex > 0 && (
            <button
              type="button"
              onClick={guide.prev}
              className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-panel transition-colors"
              aria-label="Previous step"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          )}
          {guide.isStepDone(activeStep) && !isLast && (
            <button
              type="button"
              onClick={guide.next}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
              aria-label="Next step"
            >
              <Check className="w-3.5 h-3.5" /> Next
            </button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
